/**
 * Firebase Usage Tracker (Simplified)
 * 
 * Tracks Firestore reads/writes and syncs to Firebase for dev visibility.
 * NO localStorage - data only goes to Firestore.
 * 
 * Syncs on: login, logout, visibility change (tab hidden), and every 5 minutes of activity.
 */

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Firestore pricing (as of 2024)
const PRICING = {
  writes: 0.18 / 100000,  // $0.18 per 100K writes
  reads: 0.06 / 100000,   // $0.06 per 100K reads
  deletes: 0.02 / 100000, // $0.02 per 100K deletes
};

// Sync every 5 minutes if there's activity
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

export type OperationType = 'read' | 'write' | 'delete';

export type UsageEntry = {
  timestamp: number;
  type: OperationType;
  collection: string;
  docCount: number;
  source: string;
};

// In-memory only - no localStorage
let sessionStats = {
  reads: 0,
  writes: 0,
  deletes: 0,
  startTime: Date.now(),
};

let recentOperations: UsageEntry[] = [];
const listeners: Set<() => void> = new Set();

// Sync state
let currentUserId: string | null = null;
let lastSyncedStats: { reads: number; writes: number; deletes: number } | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let isDirty = false; // Track if there are unsaved changes

// --- Core Tracking ---

export function trackOperation(
  type: OperationType,
  collection: string,
  docCount: number = 1,
  source: string = 'unknown'
): void {
  const entry: UsageEntry = { timestamp: Date.now(), type, collection, docCount, source };

  // Update counts
  if (type === 'read') sessionStats.reads += docCount;
  else if (type === 'write') sessionStats.writes += docCount;
  else sessionStats.deletes += docCount;

  // Keep last 20 for display
  recentOperations = [...recentOperations.slice(-19), entry];
  isDirty = true;
  
  notifyListeners();
}

export function trackReads(collection: string, docCount: number, source: string = 'query'): void {
  trackOperation('read', collection, docCount, source);
}

export function trackRead(collection: string, source: string = 'getDoc'): void {
  trackOperation('read', collection, 1, source);
}

export function trackWrite(collection: string, source: string = 'setDoc'): void {
  trackOperation('write', collection, 1, source);
}

export function trackDelete(collection: string, source: string = 'deleteDoc'): void {
  trackOperation('delete', collection, 1, source);
}

// --- Stats Access (for UI) ---

export type UsageStats = {
  session: { reads: number; writes: number; deletes: number; startTime: number };
  today: { reads: number; writes: number; deletes: number; date: string };
  allTime: { reads: number; writes: number; deletes: number; firstTracked: number };
  recentOperations: UsageEntry[];
};

export function getUsageStats(): UsageStats {
  const today = new Date().toISOString().split('T')[0];
  // For simplified version, session = today = allTime (all in-memory)
  return {
    session: { ...sessionStats },
    today: { ...sessionStats, date: today },
    allTime: { ...sessionStats, firstTracked: sessionStats.startTime },
    recentOperations: [...recentOperations],
  };
}

export function calculateCosts(_period: 'session' | 'today' | 'allTime'): {
  reads: number; writes: number; deletes: number; total: number;
} {
  // In simplified version, all periods use session stats (in-memory only)
  const data = sessionStats;
  const readCost = data.reads * PRICING.reads;
  const writeCost = data.writes * PRICING.writes;
  const deleteCost = data.deletes * PRICING.deletes;
  return { reads: readCost, writes: writeCost, deletes: deleteCost, total: readCost + writeCost + deleteCost };
}

export function subscribeToUsage(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notifyListeners(): void {
  listeners.forEach(fn => fn());
}

export function resetSession(): void {
  sessionStats = { reads: 0, writes: 0, deletes: 0, startTime: Date.now() };
  recentOperations = [];
  isDirty = true;
  notifyListeners();
}

export function resetAllStats(): void {
  resetSession();
}

export function getSessionRate(): { readsPerMin: number; writesPerMin: number } {
  const sessionMinutes = Math.max(1, (Date.now() - sessionStats.startTime) / 60000);
  return {
    readsPerMin: Math.round((sessionStats.reads / sessionMinutes) * 10) / 10,
    writesPerMin: Math.round((sessionStats.writes / sessionMinutes) * 10) / 10,
  };
}

export function getSessionDuration(): string {
  const ms = Date.now() - sessionStats.startTime;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

// --- Firestore Sync (only syncs if dirty) ---

async function syncToFirestore(): Promise<void> {
  if (!currentUserId || !isDirty) return;
  
  // Check if anything changed
  const currentStats = {
    reads: sessionStats.reads,
    writes: sessionStats.writes,
    deletes: sessionStats.deletes,
  };
  
  if (lastSyncedStats &&
      lastSyncedStats.reads === currentStats.reads &&
      lastSyncedStats.writes === currentStats.writes &&
      lastSyncedStats.deletes === currentStats.deletes) {
    isDirty = false;
    return;
  }
  
  try {
    const usageRef = doc(db, 'users', currentUserId, 'state', 'usage');
    await setDoc(usageRef, {
      sessionReads: sessionStats.reads,
      sessionWrites: sessionStats.writes,
      sessionDeletes: sessionStats.deletes,
      sessionStart: sessionStats.startTime,
      lastActive: serverTimestamp(),
    }, { merge: true });
    
    lastSyncedStats = currentStats;
    isDirty = false;
  } catch (err) {
    console.warn('Usage sync failed:', err);
  }
}

// --- Public Sync API ---

/**
 * Set user ID and start/stop heartbeat
 * Called on login/logout
 */
export function setUsageTrackingUser(uid: string | null): void {
  // Flush before switching users
  if (currentUserId && currentUserId !== uid) {
    syncToFirestore();
  }
  
  currentUserId = uid;
  lastSyncedStats = null;
  
  if (uid) {
    // Start heartbeat
    if (!heartbeatInterval) {
      heartbeatInterval = setInterval(() => {
        if (isDirty) syncToFirestore();
      }, HEARTBEAT_INTERVAL_MS);
    }
    // Sync immediately on login
    isDirty = true;
    syncToFirestore();
  } else {
    // Stop heartbeat on logout
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  }
}

/**
 * Force sync - call on logout or visibility change
 */
export function flushUsageToFirestore(): void {
  syncToFirestore();
}

// --- Visibility change hook ---
// Sync when tab becomes hidden (user might close tab)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && currentUserId && isDirty) {
      syncToFirestore();
    }
  });
}
